import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, Checkbox, HStack, Image, Text } from '@chakra-ui/react';
import CommentToolbarPlugin from '../plugins/CommentToolbarPlugin';
import { $getRoot } from 'lexical';
import { useSharedHistoryContext } from '../context/SharedHistoryContext';
import { CAN_USE_DOM } from '../utils/canUseDOM';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import Button from '../../../components/Button';
import { useEditorState } from '../BaseEditor/hooks/useBaseEditorFormatState';
import { BaseEditorPlugins, FloatingPlugins } from '../BaseEditor';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import VerifiedIcon from '../../../assets/verified-icon.svg';
import UserAvatar from '../../../assets/medium_default_optimized.png';
import QuoteBox from '../components/CommentsSidebar/components/CommentItem/components/QuoteBox';
import { useNavigate } from 'react-router-dom';
import { PollNode } from '../nodes/PollNode';
import './index.css';
import Cookies from 'js-cookie';
import { handleUserClick } from 'src/utils/helper.util';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

interface CommentEditorProps {
  onPost: () => void;
  onCancel: () => void;
  currentUser?: {
    id: string;
    imgUrl: string;
    firstName: string;
    isVerifiedAuthor: boolean;
    username: string;
  };
  editorStateRef: React.MutableRefObject<any>;
  content?: any;
  setIsChecked?: any;
  isChecked?: boolean;
  isHiddenCheckbox?: boolean;
  shouldOnlyResetText?: boolean;
  isDisabledCancelButton?: boolean;
  isLoading: boolean;
  replyTo?: string;
  articleId?: string;
  isEditing?: boolean;
  selectedText?: string;
}

export function CommentEditor({
  onPost,
  currentUser,
  editorStateRef,
  isLoading,
  replyTo,
  isHiddenCheckbox,
  setIsChecked,
  isChecked,
  isEditing,
  shouldOnlyResetText,
  isDisabledCancelButton = true,
  selectedText,
  onCancel
}: CommentEditorProps): JSX.Element {
  const { historyState } = useSharedHistoryContext();
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isSmallWidthViewport, setIsSmallWidthViewport] = useState<boolean>(false);
  const [editor] = useLexicalComposerContext();
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isPollEmpty, setPollEmpty] = useState(false);
  const navigate = useNavigate();
  const userId = JSON.parse(localStorage.getItem('user'))?.id;
  const editorState = useEditorState(editor as any);
  const token = Cookies.get(`token`);

  const handleClearEditor = useCallback(() => {
    if (!editor) return;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
    });
    editor.focus();
  }, [editor]);

  const currentCheckboxValue = isChecked ?? true;

  const onCheckboxChange = () => {
    if (!setIsChecked) return;
    setIsChecked(!currentCheckboxValue);
  };

  const onRef = useCallback((_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  }, []);

  useEffect(() => {
    if (editor) {
      const hasAnyContent = (node: any): boolean => {
        if (!node) return false;

        if (node.type === 'poll') {
          if (PollNode.validatePollData(node)) {
            setPollEmpty(false);
            return true;
          } else {
            setPollEmpty(true);
            return false;
          }
        }

        if (node.type === 'text' && typeof node.text === 'string') {
          return node.text.trim().length > 0;
        }

        if (node.children && Array.isArray(node.children)) {
          return node.children.some(hasAnyContent);
        }

        if (node.type && node.type !== 'paragraph') {
          return true;
        }

        return false;
      };

      const unregisterUpdateListener = editor.registerUpdateListener(({ editorState }) => {
        const nodeMap = editorState.toJSON()?.root?.children || [];

        const contentExists = nodeMap.some(hasAnyContent);

        setIsEditorEmpty(!contentExists);
        const serializedState = editorState.toJSON();
        editorStateRef.current = serializedState;
      });

      return () => {
        unregisterUpdateListener();
      };
    }
  }, [editor]);

  useEffect(() => {
    if (isEditing && editor) {
      editor.update(() => {
        const root = $getRoot();
        const lastChild = root.getLastChild();
        if (lastChild && lastChild.getType() === 'paragraph') {
          lastChild.selectEnd(); // Focus the cursor at the end of the last paragraph node
        }
      });
    }
  }, [isEditing, editor]);

  // Viewport width management
  useEffect(() => {
    const updateViewPortWidth = () => {
      const isNextSmallWidthViewport =
        CAN_USE_DOM && window.matchMedia('(max-width: 1025px)').matches;

      if (isNextSmallWidthViewport !== isSmallWidthViewport) {
        setIsSmallWidthViewport(isNextSmallWidthViewport);
      }
    };
    updateViewPortWidth();
    window.addEventListener('resize', updateViewPortWidth);

    return () => {
      window.removeEventListener('resize', updateViewPortWidth);
    };
  }, [isSmallWidthViewport]);

  return (
    <Box mb={8}>
      <Box
        p={4}
        border={'1px solid #F2F2F2'}
        rounded={'16px'}
        boxShadow={'0px 4px 16px 0px #8080801F'}
      >
        {currentUser ? (
          <HStack
            spacing={2}
            mb={4}
            w={'fit-content'}
            onClick={(e) =>
              handleUserClick(e, () => {
                if (token) {
                  navigate(`/${currentUser.username}`);
                }
              })
            }
          >
            <Avatar
              width={'32px'}
              height={'32px'}
              cursor={'pointer'}
              src={currentUser.imgUrl || UserAvatar}
            />
            <Box display={'flex'} cursor={'pointer'} alignItems={'center'} gap={1} minW={0}>
              <p className="text-sm font-[600] hover:text-growth-green truncate min-w-0 transition duration-200">
                {currentUser.firstName}
              </p>
              {currentUser.isVerifiedAuthor && <Image ml={1} src={VerifiedIcon} alt="" />}
              {currentUser.username && (
                <span className="font-[600] text-xs text-02-dim-gray">@{currentUser.username}</span>
              )}
            </Box>
          </HStack>
        ) : (
          <></>
        )}
        {selectedText && <QuoteBox quote={selectedText} />}

        <Box position="relative">
          <Box
            border="1px solid"
            borderColor="anti-flash-white"
            rounded="5px"
            mt={4}
            mb={3}
            overflow={'hidden'}
          >
            <RichTextPlugin
              contentEditable={
                <Box ref={onRef} position="relative" rounded="5px" zIndex={40}>
                  <ContentEditable className="outline-none w-full min-h-[40px] px-4 md:px-6 py-2 rich-text-editor rounded-[5px]" />
                </Box>
              }
              placeholder={
                <Text
                  position="absolute"
                  top={2.5}
                  left={6}
                  color="mid-gray"
                  fontWeight={600}
                  fontSize={'sm'}
                  maxWidth={{ base: '200px', md: '300px' }}
                  isTruncated
                >
                  {replyTo ? `Replying to ${replyTo}` : 'Share your thoughts...'}
                </Text>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />

            <BaseEditorPlugins
              historyState={historyState}
              maxLength={50000}
              shouldFocus={false}
              isCommentEditor
            />
          </Box>
          <HStack justify="space-between" width="100%">
            <CommentToolbarPlugin setIsLinkEditMode={setIsLinkEditMode} editorState={editorState} />

            {floatingAnchorElem && !isSmallWidthViewport && (
              <FloatingPlugins
                anchorElem={floatingAnchorElem}
                isLinkEditMode={isLinkEditMode}
                setIsLinkEditMode={setIsLinkEditMode}
              />
            )}

            <HStack gap={{ base: 2, md: 4 }}>
              <Button
                className="px-[10px] md:px-[14px] py-[8.5px] h-[36px] text-sm"
                variant="outline"
                disabled={isDisabledCancelButton && isEditorEmpty}
                onClick={() => {
                  if (shouldOnlyResetText) {
                    handleClearEditor();
                  } else onCancel();
                }}
              >
                Cancel
              </Button>
              <Button
                className="px-[10px] md:px-[14px] py-[8.5px] h-[36px] text-sm"
                onClick={() => {
                  if (!userId) return UnauthorizedDispatcher();
                  onPost();
                }}
                isLoading={isLoading}
                disabled={isEditorEmpty || (!isEditorEmpty && isPollEmpty)}
              >
                {isEditing ? 'Update' : 'Post'}
              </Button>
            </HStack>
          </HStack>
        </Box>
      </Box>
      {!isHiddenCheckbox && (
        <Checkbox
          variant="square"
          mt={4}
          isChecked={!currentCheckboxValue}
          onChange={onCheckboxChange}
          sx={{
            '& .chakra-checkbox__control': {
              width: '18px',
              height: '18px'
            }
          }}
        >
          <Text fontSize="sm" color="dim-gray" fontWeight={600}>
            Hide comment from my profile
          </Text>
        </Checkbox>
      )}
    </Box>
  );
}
